/**
 * Routing.
 *
 * The legacy app had no router: `showView(name)` toggled a `hidden` class on one
 * of ~30 `#view-*` divs, all of which lived in the DOM at once, and the "current
 * view" was mirrored into sessionStorage so a refresh could restore it. Real
 * routes replace all of that — the URL is the state, and deep links work.
 *
 * Route paths intentionally mirror the legacy view names (`patient-appointments`
 * -> `/patient/appointments`) so old bookmarks map predictably.
 */

import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from '../components/layout/AppShell';
import { CoAdminLanding, RequireAuth, RequireRole } from './guards';
import { ROLES } from '../context/AuthContext';

import { LoginPage } from '../features/auth/LoginPage';
import { RegisterPage } from '../features/auth/RegisterPage';
import { ForgotPasswordPage, ResetPasswordPage } from '../features/auth/ForgotPasswordPage';

import { PatientHome } from '../features/patient/PatientHome';
import { PatientDoctors } from '../features/patient/PatientDoctors';
import { PatientBooking } from '../features/patient/PatientBooking';
import { PatientAppointments } from '../features/patient/PatientAppointments';
import { PatientPrescriptions } from '../features/patient/PatientPrescriptions';
import { PatientMedications } from '../features/patient/PatientMedications';
import { MedicinesProvider } from '../features/patient/MedicineAlarmHost';
import { PatientMedicalCV } from '../features/patient/PatientMedicalCV';
import { HealthOnboardingWizard } from '../features/patient/HealthOnboardingWizard';
import { PatientTransactions } from '../features/patient/PatientTransactions';
// المرحلة 1.3: the doctor's page, booking by the hour, the doctor's queue and profile, the application.
import { DoctorProfilePage } from '../features/patient/DoctorProfilePage';
import { BookBySlot } from '../features/patient/BookBySlot';
import { DoctorQueue } from '../features/doctor/DoctorQueue';
import { DoctorProfessionalProfile, DoctorProfilePreview } from '../features/doctor/DoctorProfessionalProfile';
import { DoctorApplicationPage } from '../features/apply/DoctorApplicationPage';

import { DoctorOverview } from '../features/doctor/DoctorOverview';
import { LiveView } from '../features/doctor/live/LiveView';
import { DoctorClinics } from '../features/doctor/DoctorClinics';
import { DoctorPrescriptions } from '../features/doctor/DoctorPrescriptions';
import { DoctorOffers } from '../features/doctor/DoctorOffers';
import { DoctorVouchers } from '../features/doctor/DoctorVouchers';
import { DoctorPackages } from '../features/doctor/DoctorPackages';
import { PhotographyBooking } from '../features/doctor/PhotographyBooking';

import { ServiceBookingPage } from '../features/patient/services/ServiceBookingPage';

import { HospitalStaff } from '../features/hospital/HospitalStaff';
import { ReceptionDesk } from '../features/reception/ReceptionDesk';
import { OperationsBoard } from '../features/operations/OperationsBoard';
import { SecretaryQueue } from '../features/secretary/SecretaryQueue';

import { InstitutionOverview } from '../features/institution/InstitutionOverview';
import { InstitutionServices } from '../features/institution/InstitutionServices';
import { InstitutionBookings } from '../features/institution/InstitutionBookings';
import { InstitutionMembers } from '../features/institution/InstitutionMembers';
import { InstitutionFinance } from '../features/institution/InstitutionFinance';

import { AdminDoctors } from '../features/admin/AdminDoctors';
import { AdminRegistrations } from '../features/admin/AdminRegistrations';
import { AdminAppointments } from '../features/admin/AdminAppointments';
import { AdminPackages } from '../features/admin/AdminPackages';
import { AdminPackageRequests } from '../features/admin/AdminPackageRequests';
import { AdminPhotographyTeams } from '../features/admin/AdminPhotographyTeams';
import { AdminInstitutions } from '../features/admin/AdminInstitutions';
import { AdminTeam } from '../features/admin/AdminTeam';
import { AdminAccounts } from '../features/admin/AdminAccounts';
import { AdminAiAccess } from '../features/admin/AdminAiAccess';
import { AdminSupport } from '../features/admin/AdminSupport';
import { AdminBranding } from '../features/admin/AdminBranding';

import { TeamTasks } from '../features/team/TeamTasks';
import { SettingsPage } from '../features/settings/SettingsPage';
import { NotFoundPage } from '../features/misc/NotFoundPage';
import { LandingRedirect } from './guards';

/*
 * The seven department desks load on demand. Each is worked by one role, so
 * a receptionist should not download the rooms calendar and a nurse should not
 * download the price list; AppShell's Suspense keeps the menu on screen while
 * a desk's chunk arrives.
 */
const named = (loader, name) => lazy(() => loader().then((m) => ({ default: m[name] })));
const ContractsDashboard = named(() => import('../features/contracts/ContractsDashboard'), 'ContractsDashboard');
const AccountsDashboard = named(() => import('../features/accounts/AccountsDashboard'), 'AccountsDashboard');
const RoomsDashboard = named(() => import('../features/rooms/RoomsDashboard'), 'RoomsDashboard');
const NursingDashboard = named(() => import('../features/nursing/NursingDashboard'), 'NursingDashboard');
const LabDashboard = named(() => import('../features/departments/DepartmentDashboards'), 'LabDashboard');
const RadiologyDashboard = named(() => import('../features/departments/DepartmentDashboards'), 'RadiologyDashboard');
const PharmacyDashboard = named(() => import('../features/departments/DepartmentDashboards'), 'PharmacyDashboard');
// The central control (phase 0): the Super Admin's packages and switches.
const AdminControl = named(() => import('../features/admin/control/AdminControl'), 'AdminControl');
const InstitutionBoard = named(() => import('../features/admin/control/InstitutionBoard'), 'InstitutionBoard');
// المرحلة 1.1: the access log, the platform's own rules and team, the API keys.
const AuditLogView = named(() => import('../features/audit/AuditLogView'), 'AuditLogView');
const PlatformPolicies = named(() => import('../features/platform/PlatformPolicies'), 'PlatformPolicies');
const PlatformTeam = named(() => import('../features/platform/PlatformTeam'), 'PlatformTeam');
const PlatformHome = named(() => import('../features/platform/PlatformHome'), 'PlatformHome');
const DeveloperKeys = named(() => import('../features/developer/DeveloperKeys'), 'DeveloperKeys');
// المرحلة 1.3: the specialties, the profiles and reviews waiting, an institution's applications.
const CatalogPage = named(() => import('../features/catalog/CatalogPage'), 'CatalogPage');
// المرحلة 1.35: an institution's own price list.
const PriceListEditor = named(() => import('../features/priceList/PriceListEditor'), 'PriceListEditor');

export function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      {/* A forgotten password: the code by email or SMS, or the link in it */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* المرحلة 1.3: a doctor's application — its own token until it is approved */}
      <Route path="/apply" element={<DoctorApplicationPage />} />

      {/* Authenticated shell */}
      <Route
        element={
          <RequireAuth>
            {/* The patient's medicines: edited in «أدويتي», rung for on every screen. */}
            <MedicinesProvider>
              <AppShell />
            </MedicinesProvider>
          </RequireAuth>
        }
      >
        {/* Patient — team members share these screens, since they hold a patient account */}
        <Route
          path="/patient"
          element={<RequireRole roles={[ROLES.PATIENT, ROLES.TEAM_MEMBER, ROLES.ADMIN]} />}
        >
          <Route index element={<PatientHome />} />
          <Route path="doctors" element={<PatientDoctors />} />
          <Route path="doctors/:categoryId" element={<PatientDoctors />} />
          <Route path="book/:doctorId" element={<PatientBooking />} />
          {/* المرحلة 1.3: the doctor's page, and the hour first */}
          <Route path="doctor/:doctorId" element={<DoctorProfilePage />} />
          <Route path="book-by-slot" element={<BookBySlot />} />
          {/* Booking an institution service — a surgery, a scan, a bed.
              Same calendar idiom as a consultation, different subject. */}
          <Route path="service/:serviceId" element={<ServiceBookingPage />} />
          <Route path="appointments" element={<PatientAppointments />} />
          <Route path="prescriptions" element={<PatientPrescriptions />} />
          <Route path="medications" element={<PatientMedications />} />
          <Route path="medical-cv" element={<PatientMedicalCV />} />
          {/* المرحلة 1.2: the health profile, step by step */}
          <Route path="health-profile" element={<HealthOnboardingWizard />} />
          <Route path="transactions" element={<PatientTransactions />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Doctor */}
        <Route path="/doctor" element={<RequireRole roles={[ROLES.DOCTOR]} />}>
          <Route index element={<DoctorOverview />} />
          {/* The schedule was merged into the clinics screen, where the legacy
              build had it. Kept as a redirect so existing links and bookmarks
              land somewhere sensible rather than on the not-found page. */}
          <Route path="schedule" element={<Navigate to="/doctor/clinics" replace />} />
          <Route path="live" element={<LiveView />} />
          <Route path="clinics" element={<DoctorClinics />} />
          {/* المرحلة 1.3: the requests, and what patients read about the doctor */}
          <Route path="queue" element={<DoctorQueue />} />
          <Route path="profile" element={<DoctorProfessionalProfile />} />
          <Route path="profile/preview" element={<DoctorProfilePreview />} />
          <Route path="prescriptions" element={<DoctorPrescriptions />} />
          <Route path="offers" element={<DoctorOffers />} />
          <Route path="vouchers" element={<DoctorVouchers />} />
          <Route path="packages" element={<DoctorPackages />} />
          <Route path="photography" element={<PhotographyBooking />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Admin */}
        <Route path="/admin" element={<RequireRole roles={[ROLES.ADMIN]} />}>
          <Route index element={<AdminDoctors />} />
          <Route path="registrations" element={<AdminRegistrations />} />
          <Route path="catalog" element={<CatalogPage scope="admin" />} />
          <Route path="appointments" element={<AdminAppointments />} />
          <Route path="packages" element={<AdminPackages />} />
          <Route path="requests" element={<AdminPackageRequests />} />
          <Route path="institutions" element={<AdminInstitutions />} />
          <Route path="control" element={<AdminControl />} />
          <Route path="control/:id" element={<InstitutionBoard />} />
          <Route path="platform" element={<PlatformPolicies />} />
          <Route path="platform-team" element={<PlatformTeam />} />
          <Route path="audit" element={<AuditLogView />} />
          <Route path="photography" element={<AdminPhotographyTeams />} />
          <Route path="team" element={<AdminTeam />} />
          <Route path="accounts" element={<AdminAccounts />} />
          <Route path="ai" element={<AdminAiAccess />} />
          <Route path="support" element={<AdminSupport />} />
          <Route path="branding" element={<AdminBranding />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Institutions — a hospital, polyclinic or lab centre manager.
            Admins are admitted too so a problem can be reproduced from the
            manager's own screens rather than described over the phone. */}
        <Route
          path="/institution"
          element={<RequireRole roles={[ROLES.INSTITUTION, ROLES.ADMIN]} />}
        >
          <Route index element={<InstitutionOverview />} />
          <Route path="services" element={<InstitutionServices />} />
          <Route path="bookings" element={<InstitutionBookings />} />
          <Route path="members" element={<InstitutionMembers />} />
          <Route path="catalog" element={<CatalogPage scope="institution" />} />
          <Route path="price-list" element={<PriceListEditor />} />
          <Route path="finance" element={<InstitutionFinance />} />
          <Route path="audit" element={<AuditLogView />} />
          <Route path="developer" element={<DeveloperKeys />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/*
          The four hospital staff areas.

          Each admits the institution manager and the platform admin alongside
          its own role, for the reason the institution branch already states:
          a problem is better reproduced from the operator's own screens than
          described over the phone. A co-admin reaches /hospital because that is
          their home, but what they may actually *do* there is decided by the
          capability check inside the screen and re-checked on every request —
          being admitted to a route is not being permitted an action.
        */}
        <Route
          path="/hospital"
          element={<RequireRole roles={[ROLES.CO_ADMIN, ROLES.INSTITUTION, ROLES.ADMIN]} />}
        >
          <Route
            index
            element={
              <CoAdminLanding>
                <HospitalStaff />
              </CoAdminLanding>
            }
          />
          <Route path="staff" element={<HospitalStaff />} />
          <Route path="operations" element={<OperationsBoard />} />
          <Route path="price-list" element={<PriceListEditor />} />
          {/* A co-admin's finance and compliance roles (المرحلة 1.1): the same
              screens the owner has, each request checked against the bundle. */}
          <Route path="finance" element={<InstitutionFinance />} />
          <Route path="audit" element={<AuditLogView />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        <Route
          path="/reception"
          element={
            <RequireRole
              roles={[ROLES.RECEPTIONIST, ROLES.CO_ADMIN, ROLES.INSTITUTION, ROLES.ADMIN]}
            />
          }
        >
          <Route index element={<ReceptionDesk />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        <Route
          path="/operations"
          element={
            <RequireRole
              roles={[ROLES.OPERATIONS_MANAGER, ROLES.CO_ADMIN, ROLES.INSTITUTION, ROLES.ADMIN]}
            />
          }
        >
          <Route index element={<OperationsBoard />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        <Route
          path="/secretary"
          element={<RequireRole roles={[ROLES.SECRETARY, ROLES.INSTITUTION, ROLES.ADMIN]} />}
        >
          <Route index element={<SecretaryQueue />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/*
          The seven department desks of the 14-dashboard upgrade.

          Same admission rule as the four above: the desk's own role, plus the
          owner, the co-admin and the Super Admin, who oversee every desk. What
          any of them may *do* inside is the capability check on each request.
        */}
        {[
          ['/contracts', ROLES.CONTRACTS_MANAGER, <ContractsDashboard key="contracts" />],
          ['/accounts', ROLES.ACCOUNTS_MANAGER, <AccountsDashboard key="accounts" />],
          ['/rooms', ROLES.ROOMS_MANAGER, <RoomsDashboard key="rooms" />],
          ['/nursing', ROLES.NURSE, <NursingDashboard key="nursing" />],
          ['/lab', ROLES.LAB_TECHNICIAN, <LabDashboard key="lab" />],
          ['/radiology', ROLES.RADIOLOGIST, <RadiologyDashboard key="radiology" />],
          ['/pharmacy', ROLES.PHARMACIST, <PharmacyDashboard key="pharmacy" />],
        ].map(([path, role, screen]) => (
          <Route
            key={path}
            path={path}
            element={<RequireRole roles={[role, ROLES.CO_ADMIN, ROLES.INSTITUTION, ROLES.ADMIN]} />}
          >
            <Route index element={screen} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
        ))}

        {/* The platform's own staff (المرحلة 1.1): the institutions they cover, and their log */}
        <Route path="/platform" element={<RequireRole roles={[ROLES.PLATFORM_STAFF, ROLES.ADMIN]} />}>
          <Route index element={<PlatformHome />} />
          <Route path="audit" element={<AuditLogView />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Marketing team */}
        <Route path="/team" element={<RequireRole roles={[ROLES.TEAM_MEMBER, ROLES.ADMIN]} />}>
          <Route index element={<TeamTasks />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>
      </Route>

      {/* Send "/" to whichever home the session's role owns */}
      <Route path="/" element={<LandingRedirect />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default AppRoutes;
